import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  ClipboardCheck, Download, Trash2, Eye, ChevronLeft, ChevronRight,
  UserCheck, UserX, Wifi, WifiOff, BarChart3,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, AppButton, AppCard, AppCardContent,
  EmptyState, ConfirmModal,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./attendance.scss";

/**
 * AttendanceManagementPage — Full attendance CRUD and reporting for admins.
 * Shows all sessions across batches with filtering, search, and export.
 *
 * Route: /attendance
 */
const AttendanceManagementPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [deleteModal, setDeleteModal] = useState({ show: false, sessionId: null });
  const [exporting, setExporting] = useState(false);
  const [offlineEnabled, setOfflineEnabled] = useState(false);

  // Filters
  const [batches, setBatches] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    batchId: "",
    branchId: "",
    startDate: "",
    endDate: "",
  });

  // Fetch filter options and offline setting
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [batchRes, branchRes, offlineRes] = await Promise.all([
          apiClient.get("/batches/list?limit=200&fields=name"),
          apiClient.get("/branches").catch(() => ({ data: { branches: [] } })),
          apiClient.get("/attendance/check-offline-enabled").catch(() => ({ data: {} })),
        ]);
        setBatches(batchRes.data.batches || []);
        setBranches(branchRes.data.branches || []);
        setOfflineEnabled(offlineRes.data.offlineAttendanceEnabled === true);
      } catch { /* ignore */ }
    };
    fetchOptions();
  }, []);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (filters.batchId) params.set("batchId", filters.batchId);
      if (filters.branchId) params.set("branchId", filters.branchId);
      if (filters.startDate) params.set("startDate", filters.startDate);
      if (filters.endDate) params.set("endDate", filters.endDate);

      const { data } = await apiClient.get(`/attendance/report?${params.toString()}`);
      setSessions(data.sessions || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch {
      setToast({ show: true, message: "Failed to load attendance data.", type: "danger" });
    }
    setLoading(false);
  }, [page, filters]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.batchId) params.set("batchId", filters.batchId);
      if (filters.branchId) params.set("branchId", filters.branchId);
      if (filters.startDate) params.set("startDate", filters.startDate);
      if (filters.endDate) params.set("endDate", filters.endDate);

      const response = await apiClient.get(`/attendance/export?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `attendance_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setToast({ show: true, message: "Export failed.", type: "danger" });
    }
    setExporting(false);
  };

  const handleDelete = async () => {
    if (!deleteModal.sessionId) return;
    try {
      await apiClient.delete(`/attendance/session/${deleteModal.sessionId}`);
      setToast({ show: true, message: "Session deleted.", type: "success" });
      setDeleteModal({ show: false, sessionId: null });
      fetchSessions();
    } catch {
      setToast({ show: true, message: "Failed to delete session.", type: "danger" });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleTimeString("en-IN", {
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="attendance-page sl-page">
      <PageHeader
        icon={ClipboardCheck}
        title="Attendance Management"
        description="View, manage, and export attendance records across all batches"
        actions={
          <div className="d-flex gap-2">
            {offlineEnabled && (
              <AppButton variant="outline" size="sm" onClick={() => navigate("/attendance/offline")}>
                <WifiOff size={16} /> Mark Offline Attendance
              </AppButton>
            )}
            <AppButton variant="outline" size="sm" onClick={() => navigate("/attendance/analytics")}>
              <BarChart3 size={16} /> Analytics
            </AppButton>
            <AppButton variant="primary" size="sm" onClick={handleExport} disabled={exporting}>
              <Download size={16} /> {exporting ? "Exporting…" : "Export CSV"}
            </AppButton>
          </div>
        }
      />

      {/* Filters */}
      <AppCard className="attendance-filters-card mb-3">
        <AppCardContent>
          <div className="attendance-filters">
            <div className="attendance-filter-item">
              <label>Branch</label>
              <select
                className="form-select form-select-sm"
                value={filters.branchId}
                onChange={(e) => handleFilterChange("branchId", e.target.value)}
              >
                <option value="">All Branches</option>
                {branches.map((b) => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="attendance-filter-item">
              <label>Batch</label>
              <select
                className="form-select form-select-sm"
                value={filters.batchId}
                onChange={(e) => handleFilterChange("batchId", e.target.value)}
              >
                <option value="">All Batches</option>
                {batches.map((b) => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="attendance-filter-item">
              <label>From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
              />
            </div>
            <div className="attendance-filter-item">
              <label>To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </div>
            <div className="attendance-filter-item attendance-filter-item--action">
              <AppButton
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters({ batchId: "", branchId: "", startDate: "", endDate: "" });
                  setPage(1);
                }}
              >
                Clear Filters
              </AppButton>
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* Sessions Table */}
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : sessions.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="No attendance records"
          description="Attendance records will appear here once classes are conducted."
        />
      ) : (
        <>
          <div className="attendance-table-container">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Batch</th>
                  <th>Branch</th>
                  <th>Instructor</th>
                  <th>Duration</th>
                  <th>
                    <Wifi size={14} /> Online
                  </th>
                  <th>
                    <WifiOff size={14} /> Offline
                  </th>
                  <th>
                    <UserCheck size={14} /> Present
                  </th>
                  <th>
                    <UserX size={14} /> Absent
                  </th>
                  <th>Manual</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s._id}>
                    <td>
                      <div className="attendance-date-cell">
                        <span className="attendance-date-cell__date">{formatDate(s.sessionDate)}</span>
                        <span className="attendance-date-cell__time">
                          {formatTime(s.sessionStart)} – {formatTime(s.sessionEnd)}
                        </span>
                      </div>
                    </td>
                    <td>{s.batchName}</td>
                    <td>{s.branchName || "-"}</td>
                    <td>{s.instructorName || "-"}</td>
                    <td>{s.durationMins} min</td>
                    <td>{s.online}</td>
                    <td>{s.manual}</td>
                    <td className="attendance-cell--present">{s.present}</td>
                    <td className="attendance-cell--absent">{s.absent + s.dropped}</td>
                    <td>
                      {s.manualAttendanceCompleted ? (
                        <span className="attendance-badge attendance-badge--done">Done</span>
                      ) : (
                        <span className="attendance-badge attendance-badge--pending">Pending</span>
                      )}
                    </td>
                    <td>
                      <div className="attendance-actions">
                        <button
                          className="attendance-action-btn"
                          title="View Details"
                          onClick={() => navigate(`/attendance/session/${s._id}`)}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="attendance-action-btn attendance-action-btn--danger"
                          title="Delete Session"
                          onClick={() => setDeleteModal({ show: true, sessionId: s._id })}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="attendance-pagination">
              <span className="attendance-pagination__info">
                Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, total)} of {total}
              </span>
              <div className="attendance-pagination__controls">
                <AppButton
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={14} /> Prev
                </AppButton>
                <span className="attendance-pagination__page">
                  Page {page} of {totalPages}
                </span>
                <AppButton
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next <ChevronRight size={14} />
                </AppButton>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        show={deleteModal.show}
        onHide={() => setDeleteModal({ show: false, sessionId: null })}
        onConfirm={handleDelete}
        title="Delete Attendance Session?"
        message="This will permanently delete this attendance session and all its records. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AttendanceManagementPage;
